<script>
import {mapActions} from "pinia";
import {documentStore} from "@/store/documentStore";
import {argumentStore} from "@/store/argumentStore";
import BaseAvatar from "@/components/BaseAvatar/BaseAvatar.vue";
import {formatZonedDateTime} from "@/baseUtils";
import {CmsDocumentContentStatus} from "@/store/model/Content";

export default {
  name: "ArgumentsListItem",
  props: {
    argData: {
      type: Object
    },
    argIndex: {
      type: Number
    },
    isSelectedArg: {
      type: Boolean
    }
  },
  components: {
    BaseAvatar
  },
  data() {
    return {
      argumentText: '',
      videoDoc: null
    }
  },
  computed: {
    isPrivate() {
      return this.argData.privateSinceDateTime;
    },
    argHasContent() {
      return !!this.argData.argumentDetailed;
    },
    argumentType() {
      return this.argData.argumentDetailed ? this.argData.argumentDetailed.type : null;
    },
    videoArgPreview() {
      if (this.videoDoc && this.videoDoc.status === CmsDocumentContentStatus.PROCESSING) {
        return '/html/images/processing-video-animated.svg';
      }

      let preview = this.videoDoc ? this.videoDoc.attachments.filter((item) => {
        return item.name === 'VIDEO_PREVIEW';
      })[0] : null;

      return preview
          ? '/api/video/' + preview.hash + '/preview'
          : '/html/images/arguments/private-video.svg';
    },
    privateSinceStr() {
      return formatZonedDateTime(this.argData.privateSinceDateTime);
    },
  },
  methods: {
    ...mapActions(documentStore, [
      'getDocumentContent',
      'getTextDocumentContent'
    ]),
    ...mapActions(argumentStore, [
      'setActiveArgument',
    ]),
    getArgumentContent() {
      const argContentId = this.argData.argumentDetailed.contentId;

      if (this.argumentType === 'VIDEO') {
        this.getDocumentContent(argContentId).then((videoDoc) => {
          this.videoDoc = videoDoc.data;
        })
      } else if (this.argumentType === 'HTML_TEXT') {
        this.getTextDocumentContent(argContentId).then((documentText)=> {
          this.argumentText = documentText.data;
        });
      }
    },
    selectArgument() {
      if(this.argHasContent && !this.isPrivate) {
        this.setActiveArgument(this.argData, this.argIndex);
      }
    }
  },
  mounted() {
    if (this.argHasContent) {
      this.getArgumentContent();
    }
  }
}
</script>

<template>
<div class="arguments-list-item"
     @click="selectArgument()"
     :class="{'arguments-list-item--active': isSelectedArg, 'arguments-list-item--private': isPrivate}"
>
    <template v-if="!isPrivate">
      <div v-if="argumentType === 'VIDEO'"
           class="arguments-list-item__thumbnail arguments-list-item__thumbnail--video"
      >
        <img :src="videoArgPreview" class="preview-img" alt="">
      </div>
      <div v-else-if="argumentType === 'HTML_TEXT'"
           class="arguments-list-item__thumbnail arguments-list-item__thumbnail--text"
           v-html="argumentText"
      >
      </div>
    </template>
    <template v-else>
      <div class="arguments-list-item__thumbnail arguments-list-item__thumbnail--private">
        <span class="private-label">{{$t('argument.thisArgumentWasChangedPrivate')}} {{privateSinceStr}}</span>
      </div>
    </template>
    <span class="arguments-list-item__title">
      <template v-if="!isPrivate">
        {{argData.title}}
      </template>
      <template v-else>
        {{$t('argument.privateArgument')}}
      </template>
    </span>
  <div class="arguments-list-item__info">
    <div class="argument-item-creator" v-if="argData.creator">
      <base-avatar size="32"
                   :img-hash="argData.creator.imgHash"
                   class="argument-item-creator__avatar"
      />
      <router-link :to="{name: 'UserView', params: {id: argData.creator.id}}"
                   target="_blank"
                   class="argument-item-creator__name"
      >{{ argData.creator.name }}</router-link>
    </div>
  </div>
  <div class="arguments-list-item__rating-block">
    <div class="votes votes--agree">
<!--      <div class="votes__members">-->

<!--      </div>-->
      <font-awesome-icon :icon="['fas', 'thumbs-up']"
                         class="votes__icon"
      />
      <div class="votes__count">
        {{isPrivate ? "N/A" : argData.ratingCount.agree}}
      </div>
    </div>
    <div class="votes votes--disagree">
<!--      <div class="votes__members">-->

<!--      </div>-->
      <font-awesome-icon :icon="['fas', 'thumbs-down']"
                         class="votes__icon"
      />
      <div class="votes__count">
        {{isPrivate ? "N/A" : argData.ratingCount.disagree}}
      </div>
    </div>
  </div>
</div>
</template>

<style lang="scss" scoped>
.arguments-list-item {
  display: flex;
  flex-direction: column;
  width: 257px;
  height: 249px;
  margin: 0 2.5px;
  padding: 5px;
  border-radius: 4px;
  cursor: pointer;

  &--active {
    background: #f1e8f8;
  }

  &--private {
    cursor: default !important;
    &:hover {
      background-color: transparent !important;
    }
    .private-label {
      padding-bottom: 20px;
      font-size: 14px;
      font-weight: 600;
      color: #4f4f4f;
      text-align: center;
      line-height: 1.43;
    }

    .votes__icon,
    .votes__count{
      color: #4f4f4f !important;
    }
  }

  &:hover {
    background-color: #f1e8f8;
  }
  &__thumbnail {
    position: relative;
    margin-bottom: 5px;

    &:after {
      content: '';
      display: block;
      position: absolute;
      top: 45px;
      left: 98px;
      z-index: 1;
      min-width: 50px;
      height: 50px;
      background: url("~@/assets/img/proposal/video-play-btn-75.svg") center no-repeat;
      background-size: 100%;
    }
    &--text {
      width: 247px;
      height: 139px;
      overflow: hidden;
      margin: 0 0 5px;
      padding: 10px 15px;
      color: #fff;
      background-image: linear-gradient(119deg, #bc5bff, #8e4eba 99%);
      font-size: 16px;
      font-weight: 600;
      text-align: left;
      line-height: 1.6;
      letter-spacing: -.01px;
      word-break: break-all;
    }
    &--video {
      .preview-img {
        width: 247px;
        height: 139px;
      }
    }
    &--private {
      width: 247px;
      height: 139px;
      background: #d1d1d1;
      display: flex;
      align-items: flex-end;
      &:after {
        top: 22px;
        background: url("~@/assets/img/arguments/private-video.svg") center no-repeat;
      }
    }
   }
  &__title {
    display: inline-block;
    margin-bottom: 10px;
    font-size: 14px;
    font-weight: 600;
    line-height: 1.43;
    color: #4f4f4f;
  }
  &__info {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 10px;

    .argument-item-creator {
      display: flex;
      align-items: center;

      &__avatar {
        margin-right: 5px;
      }
      &__name {
        font-weight: 600;
        font-size: 12px;
      }
    }
  }
  &__rating-block {
    display: flex;
    justify-content: space-between;

    .votes {
      display: flex;
      align-items: center;
      &--agree {
        color: #01c77e;
        .votes__members {
          margin-right: 2px;
        }
      }
      &--disagree {
        color: #e64407;
        .votes__members {
          margin-left: 4px;
        }
      }
      &__icon {
        margin-right: 5px;
      }
      &__count {
        font-size: 12px;
      }
      &__members {
        display: flex;
        margin-left: 10px;
        .member {
          position: relative;
          left: -3px;
          width: 22px;
          height: 22px;
          background-color: #cccccc;
          border-radius: 22px;
          &:first-child {
            left: 0;
          }
          &:last-child {
            left: -6px;
          }
        }
      }
    }
  }
}
</style>